import { Json } from "@redotech/json/json";
import axios, { AxiosHeaders } from "axios";

const TOKEN_KEY = "redo.portal_auth_token";

const client = () => {
  const widgetId = getWidgetId();
  if (!widgetId) {
    throw new Error("Missing ?widget_id");
  }
  const base = `${process.env.REDO_API_URL}/widgets/${widgetId}`;
  const headers = new AxiosHeaders();
  const client = axios.create({ baseURL: base, headers });
  return client;
};

export const getWidgetId = () => {
  let widgetId = new URLSearchParams(window.location.search).get("widget_id");
  if (!widgetId && widget_id !== "replace_me") {
    widgetId = widget_id;
  }
  return widgetId;
};

const authentication = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
};

export const getOrders = (params) => {
  return client().get("orders", { headers: authentication(), params });
};

export const getOrderById = (id) => {
  return client().get(`orders/${id}`);
};

export const getReturn = (id) => {
  return client().get(`returns/${id}`, { headers: authentication() });
};

export type CreateAuthRequest =
  | { type: "order"; email: string; orderNumber: string }
  | { type: "email"; token: string };

export interface CreateAuthResponse {
  token: string;
  customer: { email: string; name: string };
}

/**
 * POST /widgets/:id/customer-auth
 */
export const createAuth = async (
  request: CreateAuthRequest,
): Promise<CreateAuthResponse> => {
  let body: Json;
  switch (request.type) {
    case "email":
      body = { token: request.token };
      break;
    case "order":
      body = { email: request.email, orderNumber: request.orderNumber };
      break;
  }
  const response = await client().post("customer-auth", body, {
    params: { type: request.type },
  });
  return response.data;
};

export interface SendAuthEmailRequest {
  email: string;
  url: string;
}

export interface SendAuthEmailResponse {}

/**
 * POST /widgets/:id/customer-auth/email
 */
export const sendAuthEmail = async (
  request: SendAuthEmailRequest,
): Promise<SendAuthEmailResponse> => {
  const response = await client().post("customer-auth/email", request);
  return response.data;
};

export const getSettings = () => {
  return client().get("");
};

export const getProduct = (id) => {
  return client().get(`products/${id}`, { headers: authentication() });
};

export const getShopifyOrder = (shopifyOrderId: number) => {
  return client().get(`shopify/order/${shopifyOrderId}`, {
    headers: authentication(),
  });
};

export const getProducts = () => {
  return client().get("shopify/products", { headers: authentication() });
};

export const getLocations = () => {
  return client().get("shopify/locations", { headers: authentication() });
};

export const getRecommendations = (productId: number) => {
  return client().get(`shopify/recommendations/${productId}`, {
    headers: authentication(),
  });
};

export const searchProducts = (searchQuery: string) => {
  return client().get(`shopify/search/${searchQuery}`, {
    headers: authentication(),
  });
};

export const getShippingFee = (params) => {
  return client().put("returns/fee", { headers: authentication(), params });
};

export const getRefundsForItems = (params) => {
  return client().put("returns/refunds", { headers: authentication(), params });
};

export const getAutomations = () => {
  return client().get("automations");
};

export const uploadImages = (params) => {
  return client().post("upload", params, {
    headers: {
      ...authentication(),
      "Content-Type": "multipart/form-data",
    },
  });
};

export const submitReturn = (params) => {
  return client().post("returns/submit", { headers: authentication(), params });
};

export const createCart = (params) => {
  return client().post("cart/create", { headers: authentication(), params });
};

export const getCart = (params) => {
  return client().post("cart/get", { headers: authentication(), params });
};

export const addItemToCart = (params) => {
  return client().post("cart/add", { headers: authentication(), params });
};

export const removeItemFromCart = (params) => {
  return client().post("cart/remove", { headers: authentication(), params });
};

export const addDiscountCodesToCart = (params) => {
  return client().post("cart/discounts", { headers: authentication(), params });
};
